import React, { useMemo } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Grid } from '~/components';
import { asyncState } from '~/hooks';
import { useDashboard } from './context';
import { ActionCell, StatusCell, OwnerCell, mapOwner } from './DashboardGridCells';
import { sortDates, sortStrings } from '~/util/sorters';
import { DateTime } from 'luxon';


const headerClassName = 'bg-gray-50 p-4 border-b text-left';
const props = {
    className: 'p-4 text-sm border-b'
};

const columns = [
    {
        title: 'Your File No.',
        field: 'filenumber',
        headerClassName,
        props
    },
    {
        title: 'Tryon File No',
        field: 'orderNumber',
        headerClassName,
        props
    },
    {
        title: 'Submitted On',
        cell: ({data}) => {
            if (data?.submitted) {
                return DateTime.fromISO(data.submitted).toUTC().toLocaleString();
            } else {
                return <></>;
            }
        },
        sorter: (a,b) => sortDates(a.submitted, b.submitted),
        headerClassName,
        props
    },
    {
        title: 'Street Address',
        field: 'streetAddress',
        headerClassName,
        cell: ({ data }) => data?.streetAddress === 'null' ? '' : data?.streetAddress,
        props
    },
    {
        title: 'Current Owner',
        cell: OwnerCell,
        sorter: sortByOwners,
        headerClassName,
        props
    },
    {
        title: 'Status',
        cell: StatusCell,
        sorter: (a,b) => {
            const titleStatus = /^t-[1-9]{1}/i;
            const searchStatus = /^p-[1-9]{1}/i;

            if (titleStatus.test(a.status) && searchStatus.test(b.status))
                return 1;
            if (searchStatus.test(a.status) && titleStatus.test(b.status))
                return -1;
            
            const statusIds = {
                a: Number(a.status.slice(2)),
                b: Number(b.status.slice(2))
            };

            return statusIds.a - statusIds.b;
        },
        width: 196,
        headerClassName,
        props
    },
    {
        title: '',
        key: 'actions',
        canSort: false,
        cell: ActionCell,
        headerClassName,
        props: {
            className: 'py-4 px-2 border-b'
        }
    }
];

const DashboardGrid = () => {

    const { gridData, filteredRows  } = useDashboard(); 
 
    const isLoading = useMemo(() => gridData.status == asyncState.PENDING, [gridData.status]);
    const rows = Array.isArray(filteredRows) ? filteredRows : [];


    return (
        <div className='w-full flex flex-col items-stretch justify-center my-4'>
            {isLoading ? (
                <AiOutlineLoading3Quarters className='text-primary-50 animate-spin m-auto' size='2rem' />
            ) : (
                <Grid 
                    usePagination
                    data={rows} 
                    columns={columns}
                    className='w-full rounded-md'
                    tableClassName='mb-2'
                />
            )}
        </div>
    );
};

export default DashboardGrid;

function sortByOwners(a,b) {
    const aOwners = Array.isArray(a.owners)
        ? a.owners.map(mapOwner).filter(o => typeof o == 'string' && o.trim().length > 0)
        : [];
    
    const bOwners = Array.isArray(a.owners)
        ? b.owners.map(mapOwner).filter(o => typeof o == 'string' && o.trim().length > 0)
        : [];

    if (aOwners.length < 1) {
        return bOwners.length < 1 ? 0 : 1;
    }

    if (bOwners.length < 1) {
        return -1;
    }

    aOwners.sort(sortStrings);
    bOwners.sort(sortStrings);

    if (aOwners[0].toLowerCase() < bOwners[0].toLowerCase())
        return -1;
    if (aOwners[0].toLowerCase() > bOwners[0].toLowerCase())
        return 1;
    return 0;
}
